/**
 * menu-hover-delay.js
 *
 * Keeps desktop nav dropdowns open for a grace period after the cursor leaves,
 * making it possible to reach submenu columns without the menu snapping shut.
 *
 * Root cause (confirmed in theme.js line 36453-36464):
 *   jQuery attaches THREE mouseleave handlers that close the dropdown:
 *     1. $('.site-header').on('mouseleave', hideDropdown)
 *     2. $('.site-nav__dropdown').on('mouseleave', hideDropdown)
 *     3. $(parent li).on('mouseleave', hideDropdown)   ← previously unblocked
 *
 *   jQuery implements .on('mouseleave') as a filtered mouseout listener
 *   (bubble phase). Blocking the mouseout event before it bubbles prevents
 *   all three handlers from firing.
 *
 * Strategy:
 *   - Attach a capture-phase mouseout listener to each parent <li>
 *     (.site-nav--has-dropdown). Because capture fires top-down BEFORE bubble,
 *     calling stopImmediatePropagation() here prevents any mouseout that
 *     originates inside the nav item — from the link text, the dropdown, or
 *     any gap — from ever reaching jQuery's three handlers.
 *   - Replace jQuery's close logic with native mouseleave (non-capture) on the
 *     parent <li>. Native mouseleave only fires when the cursor truly exits the
 *     element's boundary (including all DOM descendants), never when moving
 *     between children. This means moving between submenu columns never
 *     triggers a close.
 *   - The site-header still needs its own mouseout interception because
 *     jQuery's header handler listens on an ancestor of the nav items.
 */
(function () {
  'use strict';

  var GRACE_MS = 600;

  function isDescendant(ancestor, node) {
    return !!node && ancestor !== node && ancestor.contains(node);
  }

  function init() {
    var nav = document.getElementById('AccessibleNav');
    if (!nav) return;

    var siteHeader = document.querySelector('.site-header');
    var parents = nav.querySelectorAll('.site-nav--has-dropdown');
    if (!parents.length) return;

    /* ── close helpers ── */
    function closeAll() {
      document.querySelectorAll('.site-nav--has-dropdown.nav-hover').forEach(function (el) {
        el.classList.remove('nav-hover');
      });
      if (siteHeader && siteHeader.classList.contains('overlay-header')) {
        document.querySelectorAll('.sticky-header').forEach(function (el) {
          el.classList.remove('sticky-header-open');
        });
      }
      if (document.querySelectorAll('.nav-hover').length === 0) {
        document.querySelectorAll('.sticky-header').forEach(function (el) {
          el.classList.add('sticky-header-transformed');
        });
      }
    }

    function closeOne(el) {
      if (!el) return;
      el.classList.remove('nav-hover');
      if (siteHeader && siteHeader.classList.contains('overlay-header')) {
        document.querySelectorAll('.sticky-header').forEach(function (x) {
          x.classList.remove('sticky-header-open');
        });
      }
      if (document.querySelectorAll('.nav-hover').length === 0) {
        document.querySelectorAll('.sticky-header').forEach(function (x) {
          x.classList.add('sticky-header-transformed');
        });
      }
    }

    /* ── site-header: block jQuery's handler #1 ── */
    var headerTimer = null;

    if (siteHeader) {
      siteHeader.addEventListener('mouseout', function (e) {
        if (isDescendant(siteHeader, e.relatedTarget) || siteHeader === e.relatedTarget) return;
        e.stopImmediatePropagation();
        if (headerTimer) clearTimeout(headerTimer);
        headerTimer = setTimeout(function () { closeAll(); headerTimer = null; }, GRACE_MS);
      }, true);

      siteHeader.addEventListener('mouseover', function () {
        if (headerTimer) { clearTimeout(headerTimer); headerTimer = null; }
      }, true);
    }

    /* ── per nav-item setup ── */
    parents.forEach(function (parent) {
      if (parent.parentElement && parent.parentElement.id !== 'AccessibleNav') return;

      var timer = null;

      function cancelTimer() {
        if (timer)       { clearTimeout(timer);       timer       = null; }
        if (headerTimer) { clearTimeout(headerTimer); headerTimer = null; }
      }

      function scheduleClose() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () { closeOne(parent); timer = null; }, GRACE_MS);
      }

      /* Block jQuery handlers #2 and #3 on both the parent <li> and the
         dropdown — capture fires before bubble so stopImmediatePropagation
         here prevents jQuery's filtered-mouseout from ever running. */
      parent.addEventListener('mouseout', function (e) {
        e.stopImmediatePropagation();
      }, true);

      var dropdown = parent.querySelector(':scope > .site-nav__dropdown');
      if (dropdown) {
        dropdown.addEventListener('mouseout', function (e) {
          e.stopImmediatePropagation();
        }, true);
      }

      /* Our own close logic using native mouseleave (non-capture).
         Fires exactly once when the cursor exits the element boundary —
         never when moving between children inside it. */
      parent.addEventListener('mouseenter', cancelTimer, false);
      parent.addEventListener('mouseleave', scheduleClose, false);

      if (dropdown) {
        dropdown.addEventListener('mouseenter', cancelTimer, false);
        dropdown.addEventListener('mouseleave', scheduleClose, false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
